import { RiArrowDownSLine, RiArrowUpSLine } from '@remixicon/react';
import { FC, useState } from 'react';
import styles from './FilterDropdown.module.css';
import MenuDropDown from '../MenuDropDown';
import {
  GroupByConfig,
  GroupTooltipConfig,
  Option,
} from '../../../types/SelectInput';
import { translate } from '../../../localization';
import hasValidValues from '../../../utils/hasValidValues';

type FilterDropdownProps = {
  name: string;
  list: Option[];
  isDisabled?: boolean;
  inputPlaceholder?: string;
  searchInputPlaceholder?: string;
  required?: boolean;
  multiSelect?: boolean;
  value: string[];
  handleSelection: (value: string | string[]) => void;
  className?: string;
  isGroupDropDown?: boolean;
  groupingConfig?: GroupByConfig[];
  groupTooltips?: GroupTooltipConfig;
  unCategorizedListTitle?: string;
  enableSelectAll?: boolean;
};

const FilterDropdown: FC<FilterDropdownProps> = ({
  name,
  list,
  inputPlaceholder = '',
  searchInputPlaceholder = '',
  required = false,
  multiSelect = false,
  handleSelection,
  isDisabled,
  value,
  className,
  groupingConfig,
  groupTooltips,
  unCategorizedListTitle = translate('others'),
  isGroupDropDown = false,
  enableSelectAll = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionSelection = (selectedValue: string | string[]) => {
    if (multiSelect) {
      handleSelection(selectedValue as string[]);
    } else {
      handleSelection(selectedValue as string);
      setIsOpen(false);
    }
  };

  const getSelectedOptionLabel = () => {
    const getLabel = (option: string) =>
      list?.find(entry => entry.name === option)?.label || inputPlaceholder;

    if (multiSelect) {
      if (value?.length === 1) {
        return getLabel(value[0]);
      }

      if (value?.length === list?.length) {
        if (name === 'targetAudience' && value?.length === 2) {
          return `${inputPlaceholder} ${translate(
            'selected_both_placeholder'
          )}`;
        }

        return `${inputPlaceholder} ${translate('selected_all_placeholder')}`;
      }

      return `${(value as string[]).length} selected`;
    }

    return getLabel(value[0]);
  };

  return (
    <div className={className}>
      <div
        className={`${styles.input_wrapper} ${
          isDisabled ? styles['input_wrapper--disabled'] : ''
        }`}
        onClick={handleClick}
        id={name}
        title={inputPlaceholder}
        tabIndex={0}
        role='combobox'
        aria-expanded='false'
        aria-controls={`menu-dd-${name}`}
        aria-disabled={isDisabled}
        onKeyUp={() => {}}
      >
        <div className={styles['input_wrapper--inner']}>
          <p
            className={`${styles.selected_value} ${
              isDisabled ? styles['input_wrapper--inner--disabled'] : ''
            }`}
          >
            {value?.length > 0 && hasValidValues(value) ? (
              <span>{getSelectedOptionLabel()}</span>
            ) : (
              <>
                <span>{inputPlaceholder}</span>
                {required && <span className={styles.required}>*</span>}
              </>
            )}
          </p>
          {isOpen ? (
            <RiArrowUpSLine className={styles.down_arrow} size={16} />
          ) : (
            <RiArrowDownSLine className={styles.down_arrow} size={16} />
          )}
        </div>
      </div>
      <div className={styles.options_wrapper} id={`menu-dd-${name}`}>
        <div className={`${styles['options_wrapper--inner']}`}>
          <MenuDropDown
            selectedValue={multiSelect ? value : value?.[0] || ''}
            list={list}
            valueProperty='name'
            sortBy='label'
            isSorted
            isOpen={isOpen}
            multiSelect={multiSelect}
            onChange={handleOptionSelection}
            isShowSelectElement={false}
            onSelectTabClick={() => {
              if (!isDisabled) {
                setIsOpen(false);
              }
            }}
            wrapperClasses={isOpen ? styles['menu_dropdown--wrapper'] : ''}
            className={styles['menu_dropdown_list--wrapper']}
            searchInputPlaceholder={searchInputPlaceholder}
            groupingConfig={groupingConfig}
            groupTooltips={groupTooltips}
            unCategorizedListTitle={unCategorizedListTitle}
            categorizedDropdown={isGroupDropDown}
            enableSelectAll={enableSelectAll}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterDropdown;
